import React, { useEffect, useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import classNames from "classnames";
import WidgetCard from "../_shared/WidgetCard";
import PortalMenu from "../_shared/PortalMenu";
import { ChevronDown } from "../_shared/icons";
import { registerHeadOfficeCharts } from "../_shared/registerCharts";
import { MixDimension } from "../data";
import styles from "./style.module.scss";

registerHeadOfficeCharts();

interface ReadingMixProps {
  dimensions: MixDimension[];
  activeIdx: number;
  onChange: (idx: number) => void;
}

export default function ReadingMix({
  dimensions,
  activeIdx,
  onChange,
}: ReadingMixProps) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dim = dimensions[activeIdx];

  // the active dimension may have no real slices (no reading in this period) —
  // show a not-found body instead of a blank donut.
  const dimHasData = !!dim && dim.data.some((d) => d.pct > 0);

  const VISIBLE_COUNT = 5;
  const items = dim?.data ?? [];
  const hasMore = items.length > VISIBLE_COUNT;
  const visibleItems = showAll ? items : items.slice(0, VISIBLE_COUNT);

  // Collapse the list again whenever the selected dimension changes
  useEffect(() => {
    setShowAll(false);
  }, [activeIdx]);

  const { chartData, options } = useMemo(
    () => ({
      chartData: {
        labels: items.map((d) => d.name),
        datasets: [
          {
            data: items.map((d) => d.pct),
            backgroundColor: items.map((d) => d.color),
            borderWidth: 2,
            borderColor: "#ffffff",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        cutout: "64%",
        plugins: { legend: { display: false } },
      } as any,
    }),
    [dim],
  );

  const dropdown = (
    <div className={styles.dropWrap} ref={ref}>
      <button
        type="button"
        className={classNames(styles.dropBtn, open && styles.dropBtnOpen)}
        onClick={() => setOpen((v) => !v)}
      >
        {dim.label}
        <ChevronDown color="var(--ho-primary)" />
      </button>
      <PortalMenu
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        className={styles.menu}
      >
        {dimensions.map((d, i) => {
          const selected = i === activeIdx;
          return (
            <button
              type="button"
              key={d.key}
              className={classNames(
                styles.menuItem,
                selected && styles.menuItemActive,
              )}
              onClick={() => {
                onChange(i);
                setOpen(false);
              }}
            >
              <span>{d.label}</span>
              {selected && (
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--ho-primary)"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </button>
          );
        })}
      </PortalMenu>
    </div>
  );

  if (!dimHasData) {
    return (
      <WidgetCard title="Reading Mix" right={dropdown} stretch flush>
        <div className={styles.empty}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.emptyIcon}
          >
            <path d="M3 7l9-4 9 4-9 4-9-4z" />
            <path d="M3 7v10l9 4 9-4V7" />
            <path d="M3 12l9 4 9-4" opacity="0.5" />
          </svg>
          <div className={styles.emptyTitle}>No data found</div>
          <div className={styles.emptyHint}>
            There's nothing to show for this period yet.
          </div>
        </div>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard title="Reading Mix" right={dropdown} stretch flush>
      <div className={styles.body}>
        <div className={styles.chartBox}>
          <Doughnut data={chartData} options={options} />
        </div>
        <div className={styles.legend}>
          <div
            className={classNames(
              styles.legendList,
              showAll && styles.legendListScroll,
            )}
          >
            {visibleItems.map((m) => (
              <div className={styles.legendItem} key={m.name}>
                <span className={styles.dot} style={{ background: m.color }} />
                {/* title shows the full label on hover when it's ellipsised */}
                <span className={styles.legendName} title={m.name}>
                  {m.name}
                </span>
                <span className={styles.legendPct}>{m.pct}%</span>
              </div>
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              className={styles.seeMoreBtn}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "See less" : "See more"}
              <span
                className={classNames(
                  styles.seeMoreIcon,
                  showAll && styles.seeMoreIconOpen,
                )}
              >
                <ChevronDown color="var(--ho-primary)" />
              </span>
            </button>
          )}
        </div>
      </div>
    </WidgetCard>
  );
}
